/**
 * The GRIHA report of a video's latest run (RUNLOG V8): the Review Queue reads and edits its
 * rows, the Report tab reads it in GRIHA's layout and downloads it as Word or PDF.
 * Backend: app/api/v1/assessments.py, mounted under /api/v1/videos.
 */
import api from './api';

// Writing a Word or PDF file with every chosen frame embedded takes a few seconds on a long
// walkthrough; the shared instance's default timeout is sized for JSON reads.
const DOWNLOAD_TIMEOUT_MS = 2 * 60 * 1000;

const base = (videoId) => `/api/v1/videos/${videoId}/assessment`;

export const assessmentAPI = {
  // 200 with `assessment: null` before one has been written; 404 when the video has no
  // finished run.
  get: (videoId) => api.get(base(videoId)),

  // 202; the rows are written in the background (a few minutes). `force` writes a new
  // version when one exists.
  generate: (videoId, { force = false } = {}) => api.post(base(videoId), { force }),

  // Any of row_id, compliance, observation, action, selected [{frame, caption}].
  updateEntry: (videoId, entryId, patch) =>
    api.patch(`${base(videoId)}/entries/${entryId}`, patch),

  // action: 'approve' | 'reject' | 'reset'
  reviewEntry: (videoId, entryId, action) =>
    api.post(`${base(videoId)}/entries/${entryId}/${action}`),

  report: (videoId) => api.get(`${base(videoId)}/report`),

  // kind: 'docx' | 'pdf'. Resolves the axios response with a Blob body.
  download: (videoId, kind) =>
    api.get(`${base(videoId)}/report.${kind}`, {
      responseType: 'blob',
      timeout: DOWNLOAD_TIMEOUT_MS,
    }),
};

/** The filename the server gave a download (Content-Disposition), or `fallback`. */
export function downloadFilename(response, fallback) {
  const header = response?.headers?.['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : fallback;
}

/** Hand a downloaded Blob to the browser as a file. */
export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default assessmentAPI;
